/**
 * 10.9IT — LADO DO JOGO do painel de administração: banimento e concessões.
 *
 * Por que uma FILA e não editar o save direto: a restauração da nuvem é decidida por pontuação de
 * avanço (CloudSave.progressScore) e moeda/diamante nem contam nela — uma edição direta no blob
 * seria descartada no próximo boot. A fila é consumida AQUI, no aparelho do jogador, e entra pelo
 * Correio, que já persiste no save e sobe pra nuvem pelo caminho normal.
 *
 * Segurança: o resgate é uma função `security definer` no banco (`claim_grants`), não um UPDATE
 * livre — senão o jogador poderia editar o `payload` da própria concessão antes de resgatar.
 * Tudo tolerante: sem internet ou sem as tabelas, devolve vazio/null e o jogo segue.
 */
import { supabase } from "./supabase";

/** 10.9K7 — "wipe": ZERAR a conta inteira no aparelho (ordem do botão Zerar do painel). */
// 10.9L26 — "complete_area": marca uma área como CONCLUÍDA (todos os objetivos no alvo), o que
// libera a região seguinte pra viagem — pedido pontual do Thiago pra destravar área de um
// personagem específico sem ele precisar cumprir os objetivos de verdade.
export type GrantKind = "items" | "coins" | "diamonds" | "level" | "remove_item" | "item_level" | "wipe" | "complete_area";
/** Tipos que ESTE cliente sabe aplicar e que o banco só entrega a quem pede (supabase/admin_wipe.sql). */
const OPT_IN_KINDS = ["wipe"];

export interface GrantItem {
  defId: string;
  rarity: string;
  level: number;
  /** 10.9J4 — item de TESTE: ignora o nível mínimo pra equipar/entrar na corrente (mesmo mecanismo
   *  de item VIP/comprado, ver EquipmentInstance.vip). Sem isto, uma arma de tier alto (ex.: fim de
   *  jogo) mandada pra um personagem de nível baixo cai na bolsa mas fica travada, sem dar pra usar. */
  vip?: boolean;
}

export interface Grant {
  id: number;
  kind: GrantKind;
  /** `classId` mira uma classe/personagem específico (hoje só "remove_item" usa) — ausente = vale
   *  pra qualquer personagem, resgatado no primeiro que carregar. */
  payload: { items?: GrantItem[]; amount?: number; level?: number; classId?: string; instanceId?: string; regionId?: string };
  note: string | null;
}

export interface Ban {
  reason: string | null;
  until: string | null; // null = permanente
}

function race<T>(p: PromiseLike<T>, ms: number): Promise<T | "timeout"> {
  return Promise.race([p, new Promise<"timeout">((res) => setTimeout(() => res("timeout"), ms))]);
}

async function meuId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

/** Esta conta está banida? Expirado conta como não banido. Sem rede: não banido (não trava o boot). */
export async function fetchBan(timeoutMs = 2500): Promise<Ban | null> {
  try {
    const uid = await meuId();
    if (!uid) return null;
    const r = await race(supabase.from("bans").select("reason,until").eq("user_id", uid).maybeSingle(), timeoutMs);
    if (r === "timeout" || r.error || !r.data) return null;
    const until = typeof r.data.until === "string" ? r.data.until : null;
    if (until && Date.parse(until) < Date.now()) return null;
    return { reason: typeof r.data.reason === "string" ? r.data.reason : null, until };
  } catch {
    return null;
  }
}

/**
 * Resgata as concessões pendentes desta conta. O banco MARCA como resgatadas e devolve só as que
 * marcou — quem chama aplica exatamente essa lista. Chamar duas vezes não duplica nada.
 *
 * `classId`: a classe do personagem ATIVO agora. Uma concessão com `payload.classId` de OUTRA
 * classe (ex.: "remover item" mira um personagem específico) fica pendente — o banco só a marca
 * quando essa classe estiver ativa. Sem isto, um jogador com dois personagens podia perder a
 * concessão pra sempre: ela seria marcada resgatada no personagem ERRADO e nunca reaplicada.
 */
export async function claimGrants(classId: string | null, timeoutMs = 4000): Promise<Grant[]> {
  try {
    const uid = await meuId();
    if (!uid) return [];
    const r = await race(supabase.rpc("claim_grants", { p_class: classId, p_kinds: OPT_IN_KINDS }), timeoutMs);
    if (r === "timeout" || r.error || !Array.isArray(r.data)) return [];
    const out: Grant[] = [];
    for (const row of r.data as Record<string, unknown>[]) {
      const kind = row.kind as GrantKind;
      if (!["items", "coins", "diamonds", "level", "remove_item", "item_level", "wipe", "complete_area"].includes(kind)) continue;
      const payload = (row.payload && typeof row.payload === "object" ? row.payload : {}) as Grant["payload"];
      out.push({ id: Number(row.id), kind, payload, note: typeof row.note === "string" ? row.note : null });
    }
    return out;
  } catch {
    return [];
  }
}
